import React, {useMemo} from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import {useGetMessages} from '../../../../hooks';
import {BrowseSort, BrowseSortType, BrowseSortDirection} from '../../types';
import {makeStyles} from '@material-ui/core';
import {getDirectionStyles} from '../../../../utils/ThemeUtils';
import {Select} from '../../../../components';
import {EVENT_ACTION, EVENT_CATEGORY} from '../../../../actions/tracking.actions';

const useStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
        alignItems: 'center'
    },
    selectRoot: {
        [theme.breakpoints.down('sm')]: {
            width: '100%'
        }
    },
    select: {
        width: theme.spacing(50),
        '& input': {
            height: '41.1px',
            textTransform: 'capitalize'
        },
        [theme.breakpoints.down('sm')]: {
            flex: 1,
            width: '100%'
        }
    },
    label: {
        paddingLeft: getDirectionStyles(theme.direction)(0, theme.spacing(2)),
        paddingRight: getDirectionStyles(theme.direction)(theme.spacing(2), 0),

        [theme.breakpoints.down('sm')]: {
            display: 'none'
        }
    }
}));

interface BrowseSortOption {
    label: string;
    value: BrowseSort;
}

export interface BrowseSortByProps {
    onChange: (value: BrowseSort, name?: string) => void;
    sortBy?: string;
    sortDir?: string;
}

const BrowseSortBy: React.FC<BrowseSortByProps> = ({sortBy, sortDir, onChange = () => {}}) => {
    const styles = useStyles();
    const messages = useGetMessages({
        relevance: 'epic.affiliate.browse.sort_type.relevance',
        newRelease: 'epic.affiliate.browse.sort_type.new_release',
        comingSoon: 'epic.affiliate.browse.sort_type.coming_soon',
        alphabetical: 'epic.affiliate.browse.sort_type.alphabetical',
        priceLowToHigh: 'epic.affiliate.browse.sort_type.price_desc',
        priceHighToLow: 'epic.affiliate.browse.sort_type.price_asc',
        sortBy: 'epic.affiliate.earnings.sort_by'
    });

    const options: BrowseSortOption[] = useMemo(
        () => [
            {
                label: messages.relevance,
                value: {sortDir: BrowseSortDirection.DESC, sortBy: BrowseSortType.RELEVANCE}
            },
            {
                label: messages.newRelease,
                value: {sortDir: BrowseSortDirection.DESC, sortBy: BrowseSortType.RELEASE_DATE}
            },
            {
                label: messages.comingSoon,
                value: {sortDir: BrowseSortDirection.DESC, sortBy: BrowseSortType.COMING_SOON}
            },
            {
                label: messages.alphabetical,
                value: {sortDir: BrowseSortDirection.ASC, sortBy: BrowseSortType.TITLE}
            },
            {
                label: messages.priceLowToHigh,
                value: {sortDir: BrowseSortDirection.ASC, sortBy: BrowseSortType.CURRENT_PRICE}
            },
            {
                label: messages.priceHighToLow,
                value: {sortDir: BrowseSortDirection.DESC, sortBy: BrowseSortType.CURRENT_PRICE}
            }
        ],
        []
    );

    const selectedValue = useMemo(() => {
        const selectedIndex = options.findIndex(
            option => option.value.sortBy === sortBy && option.value.sortDir === sortDir
        );

        const defaultValue = options[0];
        const value = options[selectedIndex];

        return (value || defaultValue).label;
    }, [sortBy, sortDir]);

    return (
        <Box className={styles.root}>
            <Typography className={styles.label} variant="body2" color="textSecondary">
                {messages.sortBy}
            </Typography>

            <Select
                size="small"
                value={selectedValue}
                wrapperClassName={styles.selectRoot}
                className={styles.select}
                options={options}
                onChange={option => onChange(option.value as BrowseSort)}
                trackEventProps={{
                    interactionType: 'Click',
                    eventCategory: EVENT_CATEGORY.MediaCreator,
                    eventAction: EVENT_ACTION.ClickBrowseGamesSort
                }}
            />
        </Box>
    );
};

export default BrowseSortBy;
